import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useAuth } from '../../contexts/AuthContext';
import { emailMonitorAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface DnsRecord {
  id: number;
  domain: string;
  record_type: string;
  record_value: string | null;
  selector: string | null;
  is_valid: boolean;
  issues: string[];
  recommendations: string[];
  raw_records: any;
  scanned_at: string;
}

const RECORD_INFO: Record<string, { label: string; icon: string; description: string }> = {
  spf: { label: 'SPF', icon: 'bi-shield-check', description: 'Sender Policy Framework - Specifies which mail servers are authorized to send email for your domain' },
  dkim: { label: 'DKIM', icon: 'bi-key', description: 'DomainKeys Identified Mail - Adds a digital signature to emails to verify they were not altered in transit' },
  dmarc: { label: 'DMARC', icon: 'bi-shield-lock', description: 'Domain-based Message Authentication - Tells receivers what to do with emails that fail SPF/DKIM checks' },
  mx: { label: 'MX', icon: 'bi-envelope', description: 'Mail Exchange - Specifies the mail servers responsible for receiving email for your domain' },
};

const EmailDnsRecordsPage: React.FC = () => {
  useDocumentTitle('DNS Records');
  const { currentAsset } = useAuth();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<DnsRecord[]>([]);
  const [grouped, setGrouped] = useState<Record<string, Record<string, DnsRecord[]>>>({});
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  const fetchRecords = useCallback(async () => {
    if (!currentAsset) return;
    try {
      setLoading(true);
      const response = await emailMonitorAPI.getDnsRecords(currentAsset.id);
      if (response.data.status === 'success') {
        setRecords(response.data.data.records || []);
        setGrouped(response.data.data.grouped || {});
      }
    } catch (error) {
      console.error('Failed to fetch DNS records:', error);
      toast.error('Failed to load DNS records');
    } finally {
      setLoading(false);
    }
  }, [currentAsset]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const toggleExpanded = (key: string) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (!currentAsset) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <i className="bi bi-envelope-check" style={{ fontSize: '3rem', color: '#94a3b8' }}></i>
            <p className="mt-3 text-muted">Please select an asset to view DNS records</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
            <p className="mt-3 text-muted">Loading DNS records...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (records.length === 0) {
    return (
      <DashboardLayout>
        <div className="container-fluid p-0">
          <h4 className="mb-1 fw-bold">DNS Records</h4>
          <p className="text-muted mb-4">SPF, DKIM, DMARC, and MX record analysis</p>
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <i className="bi bi-search" style={{ fontSize: '3rem', color: '#94a3b8' }}></i>
              <h5 className="mt-3">No Scan Data</h5>
              <p className="text-muted">Run a scan from the Email Security Dashboard to check your DNS records.</p>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid p-0">
        <div className="mb-4">
          <h4 className="mb-1 fw-bold">DNS Records</h4>
          <p className="text-muted mb-0">SPF, DKIM, DMARC, and MX record analysis for your domains</p>
        </div>

        {Object.entries(grouped).map(([domain, types]) => (
          <div key={domain} className="mb-4">
            <h6 className="text-muted mb-3">
              <i className="bi bi-globe me-2"></i>{domain}
            </h6>

            {(['spf', 'dkim', 'dmarc', 'mx'] as const).map((recordType) => {
              const info = RECORD_INFO[recordType];
              const recs = types[recordType] || [];
              if (recs.length === 0) return null;

              const hasIssues = recs.some((r) => r.issues && r.issues.length > 0);
              const allValid = recs.every((r) => r.is_valid);
              const statusColor = allValid ? 'success' : hasIssues ? 'danger' : 'warning';
              const cardKey = `${domain}-${recordType}`;

              return (
                <div key={cardKey} className="card border-0 shadow-sm mb-3">
                  <div
                    className="card-header bg-white border-0 d-flex justify-content-between align-items-center cursor-pointer"
                    onClick={() => toggleExpanded(cardKey)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div className={`rounded-circle d-flex align-items-center justify-content-center bg-${statusColor}-subtle`} style={{ width: '40px', height: '40px' }}>
                        <i className={`${info.icon} text-${statusColor}`}></i>
                      </div>
                      <div>
                        <h6 className="mb-0 fw-bold">{info.label}</h6>
                        <small className="text-muted">{info.description}</small>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {allValid ? (
                        <span className="badge bg-success-subtle text-success"><i className="bi bi-check-circle me-1"></i>Passed</span>
                      ) : hasIssues ? (
                        <span className="badge bg-danger-subtle text-danger"><i className="bi bi-x-circle me-1"></i>Issues Found</span>
                      ) : (
                        <span className="badge bg-warning-subtle text-warning"><i className="bi bi-exclamation-triangle me-1"></i>Warnings</span>
                      )}
                      <i className={`bi bi-chevron-${expandedCards.has(cardKey) ? 'up' : 'down'}`}></i>
                    </div>
                  </div>

                  {expandedCards.has(cardKey) && (
                    <div className="card-body pt-0">
                      {recs.map((rec, idx) => (
                        <div key={rec.id || idx} className={`${idx > 0 ? 'mt-3 pt-3 border-top' : ''}`}>
                          {rec.selector && (
                            <div className="mb-2">
                              <small className="text-muted">Selector: <strong>{rec.selector}</strong></small>
                            </div>
                          )}

                          {/* Record Value */}
                          <div className="mb-3">
                            <label className="form-label fw-semibold mb-1">
                              <small>Record Value</small>
                            </label>
                            {rec.record_value ? (
                              <div className="bg-light rounded p-2" style={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                                {rec.record_value}
                              </div>
                            ) : (
                              <div className="text-danger"><i className="bi bi-x-circle me-1"></i>Not found</div>
                            )}
                          </div>

                          {/* Issues */}
                          {rec.issues && rec.issues.length > 0 && (
                            <div className="mb-3">
                              <label className="form-label fw-semibold mb-1 text-danger">
                                <small><i className="bi bi-exclamation-circle me-1"></i>Issues</small>
                              </label>
                              <ul className="list-unstyled mb-0">
                                {rec.issues.map((issue, i) => (
                                  <li key={i} className="d-flex align-items-start gap-2 mb-1">
                                    <i className="bi bi-x-circle-fill text-danger mt-1" style={{ fontSize: '0.75rem' }}></i>
                                    <small>{issue}</small>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Recommendations */}
                          {rec.recommendations && rec.recommendations.length > 0 && (
                            <div className="mb-2">
                              <label className="form-label fw-semibold mb-1 text-warning">
                                <small><i className="bi bi-lightbulb me-1"></i>Recommendations</small>
                              </label>
                              <ul className="list-unstyled mb-0">
                                {rec.recommendations.map((rec_text, i) => (
                                  <li key={i} className="d-flex align-items-start gap-2 mb-1">
                                    <i className="bi bi-arrow-right-circle text-warning mt-1" style={{ fontSize: '0.75rem' }}></i>
                                    <small>{rec_text}</small>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          <small className="text-muted">Last scanned: {formatDate(rec.scanned_at)}</small>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
};

export default EmailDnsRecordsPage;
